import { useAdminApi } from "../lib/api";
import { useI18n, type TranslationKey } from "../i18n";

/**
 * 合約
 * ═══════════════════════════════════════════════════════════════
 *
 * 唯讀。管理員不能改動合約 —— 那是雙方之間的約定，
 * 平台介入修改它，等於平台在替其中一方做決定，
 * 而我們明說了不做仲裁。
 *
 * 這一頁的用途是**看平台到底有沒有在運轉**。因為不經手款項，
 * 沒有金流報表可以看，成交數與里程碑進度就是唯一的營運指標。
 */

interface AdminContract {
  id: string;
  market: string;
  jobId: string;
  clientId: string;
  freelancerId: string;
  totalValueMinor: number;
  status: string;
  startedAt: string;
  endedAt: string | null;
  milestoneCount: number;
  approvedCount: number;
}

/* 模組層存 key，不存文字 —— 求值的時候語系還不存在 */
const STATUS_KEY: Record<string, TranslationKey> = {
  active: "ac.active",
  completed: "ac.completed",
  cancelled: "ac.cancelled",
  disputed: "ac.disputed",
};

export default function Contracts() {
  const { t, locale } = useI18n();
  const { data, loading, error } = useAdminApi<{ items: AdminContract[]; total: number }>(
    "/contracts?pageSize=50",
  );

  if (loading) return <div className="h-96 rounded-card shimmer" />;
  if (error) return <p className="rounded-card nm-inset p-6 text-sm text-brand-300">{error}</p>;

  const items = data?.items ?? [];

  return (
    <div>
      <p className="text-xs text-ink-muted">{t("ac.total", { n: data?.total ?? 0 })}</p>

      {items.length === 0 ? (
        <div className="mt-4 rounded-card nm-inset p-8 text-center">
          <p className="text-sm font-medium text-ink">{t("ac.empty")}</p>
          <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-ink-muted">
            {t("ac.emptyBody")}
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-2.5">
          {items.map((contract) => (
            <div key={contract.id} className="rounded-card nm p-5">
              <div className="flex flex-wrap items-baseline justify-between gap-3">
                <span className="text-sm font-semibold text-ink">
                  ${Math.round(contract.totalValueMinor / 100).toLocaleString()}
                </span>
                <span className="rounded-pill nm-sm px-2.5 py-1 text-[10px] font-medium text-ink-soft">
                  {STATUS_KEY[contract.status] ? t(STATUS_KEY[contract.status]) : contract.status}
                </span>
              </div>

              {/* 進度條。合約有沒有在動，比合約數更能說明問題。 */}
              <div className="mt-3 h-1.5 overflow-hidden rounded-pill nm-inset-sm">
                <div
                  className="h-full rounded-pill bg-brand-600"
                  style={{
                    width: `${
                      contract.milestoneCount
                        ? (contract.approvedCount / contract.milestoneCount) * 100
                        : 0
                    }%`,
                  }}
                />
              </div>

              <p className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-muted">
                <span>{contract.market}</span>
                <span>
                  {t("ac.milestones", {
                done: contract.approvedCount,
                total: contract.milestoneCount,
              })}
                </span>
                <span>
                  {t("ac.startedOn", {
                    date: new Date(contract.startedAt).toLocaleDateString(locale),
                  })}
                </span>
                {contract.endedAt && (
                  <span>
                    {t("ac.endedOn", {
                      date: new Date(contract.endedAt).toLocaleDateString(locale),
                    })}
                  </span>
                )}
              </p>

              <p className="mt-2 font-mono text-[10px] text-ink-muted">
                {contract.clientId.slice(0, 12)}… → {contract.freelancerId.slice(0, 12)}…
              </p>
            </div>
          ))}
        </div>
      )}

      <p className="mt-6 rounded-2xl nm-inset px-5 py-4 text-[11px] leading-relaxed text-ink-muted">
        {t("ac.readOnly")}
      </p>
    </div>
  );
}
